import React from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Chip,
  FormHelperText,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ptBrLocale from 'date-fns/locale/pt-BR';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import InfoIcon from '@mui/icons-material/Info';
import {
  getDaysAdded,
  getNextBusinessDayInRange,
  isValidDate,
  validateAllowedDays,
} from 'utils/date';

function DatepickerComponent({
  allowedWeekDays,
  change,
  className,
  disabled,
  fullWidth,
  id,
  label,
  margin,
  maxDate,
  minDate,
  name,
  toolbarText,
  value,
  variant,
}) {
  const theme = useTheme();
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const variantMobile = mobileBreakpoint ? 'dialog' : 'inline';
  let dialogProps = {};

  if (variantMobile === 'dialog') {
    dialogProps = {
      cancelLabel: null,
      okLabel: null,
    };
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={ptBrLocale}
    >
      <DatePicker
        allowKeyboardControl={false}
        autoOk
        className={className}
        disablePast
        disabled={disabled}
        fullWidth={fullWidth}
        id={id}
        inputProps={{ readOnly: true }}
        inputVariant={variant}
        inputFormat="dd/MM/yyyy"
        margin={margin}
        maxDate={maxDate}
        maxDateMessage="Data posterior as disponíveis"
        minDate={minDate}
        minDateMessage="Data anterior as disponíveis"
        name={name}
        renderInput={(props) => <TextField {...props} />}
        shouldDisableDate={(date) =>
          !validateAllowedDays(date, allowedWeekDays)
        }
        value={value}
        variant={variantMobile}
        ToolbarComponent={() => {
          if (toolbarText.length === 0) return null;

          return (
            <Box borderBottom={1}>
              <Toolbar variant="dense">
                <Box display="flex" justifyContent="center" width="100%">
                  <Chip
                    size="small"
                    icon={<InfoIcon />}
                    label={toolbarText}
                    color="secondary"
                  />
                </Box>
              </Toolbar>
            </Box>
          );
        }}
        label={
          <Typography color="textPrimary" variant="subtitle1">
            {label}
          </Typography>
        }
        onChange={(date) => {
          if (isValidDate(date)) change(date.toISOString());
        }}
        invalidDateMessage={
          <FormHelperText>Formato de data inválida</FormHelperText>
        }
        {...dialogProps}
      />
    </LocalizationProvider>
  );
}

DatepickerComponent.propTypes = {
  id: PropTypes.string,
  value: PropTypes.instanceOf(Date),
  change: PropTypes.func,
  variant: PropTypes.string,
  name: PropTypes.string,
  margin: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.node,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  allowedWeekDays: PropTypes.arrayOf(Number),
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  toolbarText: PropTypes.string,
};

DatepickerComponent.defaultProps = {
  id: '',
  value: new Date(),
  variant: 'standard',
  name: '',
  change: () => {},
  margin: 'none',
  disabled: false,
  label: 'Data de início',
  className: '',
  fullWidth: false,
  allowedWeekDays: [1],
  maxDate: null,
  minDate: getNextBusinessDayInRange(
    getDaysAdded(new Date(), parseInt(process.env.DAYS_BEFORE_BOOKING, 10))
  ),
  toolbarText: '',
};

export default DatepickerComponent;
